import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// ✅ Clean file names
import before1 from '../assets/before1.jpg';
import after1 from '../assets/after1.jpg';
import before2 from '../assets/before2.jpg';
import after2 from '../assets/after2.jpg';
import before3 from '../assets/before3.jpg';
import after3 from '../assets/after3.jpg';

const InstallGallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // ✅ 3 pairs of before/after installs
  const installs = [
    {
      before: before1,
      after: after1,
      title: "Lighting Upgrade",
      vehicle: "Gen 2 Tacoma",
      description: "LED light bar and fog light installation",
      productHandles: [
        "bilstein-6112-series-front-shock-kit-for-2005",
        "falken-at3w-all-terrain-tires", // need the exact URL to confirm
        "method-race-wheels-nv-305"
      ]
      
      
    },
    {
      before: before2,
      after: after2,
      title: "Lift Kit Install",
      vehicle: "Gen 3 Tacoma",
      description: "3-inch lift with upgraded suspension",
      productHandles: [
        "toyota-xl-linkable-bumper-light-kit-toyota-2016-23-tacoma",
        "copy-of-2005-current-tacoma-extended-travel-front-coilover-shock-kit", // <-- real handle
        "front-bumper-installation"
      ]
    },
    {
      before: before3,
      after: after3,
      title: "Armor Package",
      vehicle: "Gen 4 Tacoma",
      description: "Full skid plate and rock slider setup",
      productHandles: [
        "method-race-wheels-nv-305",
        "bfgoodrich-all-terrain-ta-ko2", // confirm exact from storefront URL
        "billet-strut-reinforcement-plate-and-lift-leveling-kit-for-4th-gen-toyota-tacoma-2024-pair"
      ]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % installs.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + installs.length) % installs.length);
  };

  return (
    <section className="py-16 text-black border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div 
            className="rounded-2xl shadow-lg px-6 py-8 md:px-10 md:py-10 mb-8"
            style={{ backgroundColor: '#ffffff' }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-black mb-4 drop-shadow">
              Before & After: <span className="text-brandred">Real Installs</span> by Our Team
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              See the transformation. Every install is done with precision and care by our certified technicians.
            </p>
          </div>
        </motion.div>

        {/* White Card Container */}
        <div 
          className="
            rounded-3xl
            shadow-[0_20px_50px_rgba(0,0,0,0.15)]
            p-8 md:p-12
            max-w-7xl mx-auto
            relative
            z-20
          "
          style={{ backgroundColor: '#ffffff' }}
        >
          <div className="relative">
            {/* Carousel */}
            <div className="overflow-hidden rounded-xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="relative"
                >
                  <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Navigation Buttons - Centered on images */}
                    <button
                      onClick={prevSlide}
                      className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-brandred border-2 border-brandred rounded-full flex items-center justify-center text-white hover:bg-[#E63B3B] hover:border-[#E63B3B] transition-all duration-300 shadow-lg hover:shadow-xl"
                      aria-label="Previous slide"
                    >
                      <ChevronLeft size={20} className="md:w-6 md:h-6" />
                    </button>
                    
                    <button
                      onClick={nextSlide}
                      className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-brandred border-2 border-brandred rounded-full flex items-center justify-center text-white hover:bg-[#E63B3B] hover:border-[#E63B3B] transition-all duration-300 shadow-lg hover:shadow-xl"
                      aria-label="Next slide"
                    >
                      <ChevronRight size={20} className="md:w-6 md:h-6" />
                    </button>
                    {/* Before Image */}
                    <div className="relative">
                      <img
                        src={installs[currentSlide].before}
                        alt={`Before: ${installs[currentSlide].title} on ${installs[currentSlide].vehicle}`}
                        className="w-full h-64 md:h-80 object-cover rounded-lg shadow-md"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4 bg-black/80 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                        BEFORE
                      </div>
                    </div>

                    {/* After Image */}
                    <div className="relative">
                      <img
                        src={installs[currentSlide].after}
                        alt={`After: ${installs[currentSlide].title} on ${installs[currentSlide].vehicle}`}
                        className="w-full h-64 md:h-80 object-cover rounded-lg shadow-md"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4 bg-brandred text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                        AFTER
                      </div>
                    </div>
                  </div>

                  {/* Install Info */}
                  <div className="flex justify-center">
                    <div className="
                      bg-gray-50
                      rounded-2xl
                      px-8 py-6
                      md:px-10 md:py-8
                      max-w-xl
                      w-full
                      flex flex-col
                      items-center
                      text-center
                    ">
                      <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                        {installs[currentSlide].title} – {installs[currentSlide].vehicle}
                      </h3>
                      <p className="text-gray-600 mb-6 text-base md:text-lg">
                        {installs[currentSlide].description}
                      </p>
                      <motion.a
                        href={`/shop?products=${installs[currentSlide].productHandles.join(",")}`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="
                          bg-brandred
                          text-offwhite
                          font-semibold
                          px-8 py-3
                          rounded-lg
                          shadow-md
                          hover:bg-[#E63B3B]
                          hover:shadow-lg
                          transition-all
                          duration-200
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                        "
                      >
                        <ExternalLink size={16} />
                        View Products Used
                      </motion.a>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dots */}
            <div className="flex justify-center mt-6 md:mt-8 gap-2">
              {installs.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 md:w-3.5 md:h-3.5 rounded-full transition-all duration-300 shadow-sm ${
                    index === currentSlide 
                      ? 'bg-brandred scale-125 shadow-md' 
                      : 'bg-gray-500 hover:bg-gray-700 border border-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstallGallery;
